import type { FaceLandmarkerResult, HandLandmarkerResult, ImageSource } from '@mediapipe/tasks-vision';

export interface LocalVisionSummary {
  kind: 'palm' | 'face';
  imageWidth: number;
  imageHeight: number;
  detectedCount: number;
  landmarkCount: number;
  promptText: string;
}

export type LocalVisionErrorStage = 'image-decode' | 'wasm-load' | 'model-load' | 'runtime' | 'unknown';

export interface LocalVisionErrorDescription {
  stage: LocalVisionErrorStage;
  message: string;
}

const LOCAL_WASM_PATH = '/mediapipe/wasm';
const REMOTE_WASM_PATH = 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@1.0.1/wasm';
const WASM_PATHS = [LOCAL_WASM_PATH, REMOTE_WASM_PATH] as const;
const HAND_MODEL_PATH = 'https://storage.googleapis.com/mediapipe-models/hand_landmarker/hand_landmarker/float16/1/hand_landmarker.task';
const FACE_MODEL_PATH = 'https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task';

type VisionModule = typeof import('@mediapipe/tasks-vision');

interface HandLandmarkerInstance {
  detect(image: ImageSource): HandLandmarkerResult;
}

interface FaceLandmarkerInstance {
  detect(image: ImageSource): FaceLandmarkerResult;
}

let visionModulePromise: Promise<VisionModule> | null = null;
let handLandmarkerPromise: Promise<HandLandmarkerInstance> | null = null;
let faceLandmarkerPromise: Promise<FaceLandmarkerInstance> | null = null;

function loadVisionModule(): Promise<VisionModule> {
  visionModulePromise ??= import('@mediapipe/tasks-vision');
  return visionModulePromise;
}

function loadImage(dataUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.decoding = 'async';
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('图片无法解码，请换一张清晰的 JPG、PNG 或 WebP 图片。'));
    image.src = dataUrl;
  });
}

function errorText(cause: unknown): string {
  return cause instanceof Error ? cause.message : typeof cause === 'string' ? cause : '';
}

export function describeLocalVisionError(cause: unknown): LocalVisionErrorDescription {
  const message = errorText(cause);
  const normalized = message.toLowerCase();
  if (normalized.includes('无法解码') || normalized.includes('decode')) {
    return { stage: 'image-decode', message: '图片无法解码，请换一张清晰的 JPG、PNG 或 WebP 图片。' };
  }
  if (normalized.includes('webassembly') || normalized.includes('wasm')) {
    return { stage: 'wasm-load', message: 'MediaPipe WebAssembly 加载失败，请确认浏览器允许 WebAssembly，并从 HTTPS 或 localhost 打开页面。' };
  }
  if (normalized.includes('fetch') || normalized.includes('network') || normalized.includes('cache') || normalized.includes('model') || normalized.includes('模型') || normalized.includes('storage.googleapis')) {
    return { stage: 'model-load', message: 'MediaPipe 模型文件加载失败，可能是网络、缓存或跨域限制；请重试或检查网络后再试。' };
  }
  if (normalized.includes('gpu') || normalized.includes('landmarker') || normalized.includes('detect') || normalized.includes('runtime')) {
    return { stage: 'runtime', message: 'MediaPipe 在当前浏览器运行失败，可能与 GPU、驱动或设备资源有关；请重启浏览器后再试。' };
  }
  return { stage: 'unknown', message: 'MediaPipe 初始化失败（未知阶段），请检查浏览器的 WebAssembly、网络和设备资源设置后重试。' };
}

async function getVisionFileset(FilesetResolver: VisionModule['FilesetResolver']) {
  let lastError: unknown;
  for (const wasmPath of WASM_PATHS) {
    try {
      return await FilesetResolver.forVisionTasks(wasmPath);
    } catch (error) {
      lastError = error;
    }
  }
  throw lastError instanceof Error ? lastError : new Error('MediaPipe WebAssembly 加载失败。');
}

async function fetchModelBuffer(modelPath: string): Promise<Uint8Array> {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 45_000);
  try {
    const response = await fetch(modelPath, { cache: 'force-cache', credentials: 'omit', signal: controller.signal });
    if (!response.ok) throw new Error(`MediaPipe 模型加载失败（HTTP ${response.status}）。`);
    return new Uint8Array(await response.arrayBuffer());
  } catch (error) {
    if (controller.signal.aborted) throw new Error('MediaPipe 模型加载超时，请检查网络后重试。');
    throw error;
  } finally {
    window.clearTimeout(timeout);
  }
}

async function getHandLandmarker() {
  handLandmarkerPromise ??= loadVisionModule().then(async ({ FilesetResolver, HandLandmarker }) => {
    const vision = await getVisionFileset(FilesetResolver);
    const modelAssetBuffer = await fetchModelBuffer(HAND_MODEL_PATH);
    return HandLandmarker.createFromOptions(vision, {
      baseOptions: { modelAssetBuffer, delegate: 'CPU' },
      numHands: 2,
      runningMode: 'IMAGE'
    });
  }).catch((error) => {
    handLandmarkerPromise = null;
    throw error;
  });
  return handLandmarkerPromise;
}

async function getFaceLandmarker() {
  faceLandmarkerPromise ??= loadVisionModule().then(async ({ FilesetResolver, FaceLandmarker }) => {
    const vision = await getVisionFileset(FilesetResolver);
    const modelAssetBuffer = await fetchModelBuffer(FACE_MODEL_PATH);
    return FaceLandmarker.createFromOptions(vision, {
      baseOptions: { modelAssetBuffer, delegate: 'CPU' },
      numFaces: 2,
      runningMode: 'IMAGE'
    });
  }).catch((error) => {
    faceLandmarkerPromise = null;
    throw error;
  });
  return faceLandmarkerPromise;
}

export function summarizeLocalVision(
  kind: 'palm' | 'face',
  imageWidth: number,
  imageHeight: number,
  detectedCount: number,
  landmarkCount: number
): LocalVisionSummary {
  const label = kind === 'palm' ? '手部' : '面部';
  const promptText = [
    `本机视觉结构摘要（${label}，不是身份识别，也不是传统断语）：`,
    `图片尺寸：${imageWidth} × ${imageHeight}；检测到${label}数量：${detectedCount}；每个目标的关键点数量：${landmarkCount || '未获得'}。`,
    '关键点只用于描述图像中是否存在可分析的轮廓与结构，不推断身份、健康、性格、财富或未来。若检测数量为 0，应以图片模糊、角度或遮挡等限制解释，不要臆测。'
  ].join('\n');
  return { kind, imageWidth, imageHeight, detectedCount, landmarkCount, promptText };
}

export async function analyzeLocalImage(kind: 'palm' | 'face', dataUrl: string): Promise<LocalVisionSummary> {
  const image = await loadImage(dataUrl);
  if (kind === 'palm') {
    const landmarker = await getHandLandmarker();
    const result = landmarker.detect(image);
    return summarizeLocalVision(kind, image.naturalWidth || image.width, image.naturalHeight || image.height, result.landmarks.length, result.landmarks[0]?.length ?? 0);
  }
  const landmarker = await getFaceLandmarker();
  const result = landmarker.detect(image);
  return summarizeLocalVision(kind, image.naturalWidth || image.width, image.naturalHeight || image.height, result.faceLandmarks.length, result.faceLandmarks[0]?.length ?? 0);
}

export const localVisionAssetInfo = {
  wasmPath: LOCAL_WASM_PATH,
  fallbackWasmPath: REMOTE_WASM_PATH,
  handModelPath: HAND_MODEL_PATH,
  faceModelPath: FACE_MODEL_PATH
};
